import { useState, useEffect, useMemo } from 'react';
import { getActivitiesByFarm, createActivity, updateActivity, deleteActivity } from '../services/api';

export const useActivities = (farmId, setMessage) => {
    const [activities, setActivities] = useState([]);
    const [activityDialogOpen, setActivityDialogOpen] = useState(false);
    const [editingActivity, setEditingActivity] = useState(null);
    const [activityForm, setActivityForm] = useState({
        stand_id: null,
        activity_type: 'Planting',
        sub_type: '',
        status: 'planned',
        planned_year: new Date().getFullYear(),
        planned_quarter: 1,
        priority: 'medium',
        performer_type: 'contractor',
        description: ''
    });
    const [financialItems, setFinancialItems] = useState([]);
    const [calculationParams, setCalculationParams] = useState({});
    
    const [filters, setFilters] = useState({
        years: [],
        standIds: [],
        activityTypes: [],
        statuses: [],
        priorities: []
    });
    const [sortBy, setSortBy] = useState('date');
    const [highlightedStandIds, setHighlightedStandIds] = useState([]);

    const loadActivities = async (id) => {
        if (!id) {
            setActivities([]);
            return;
        }
        try {
            const fetchedActivities = await getActivitiesByFarm(id);
            setActivities(fetchedActivities);
        } catch (err) {
            console.error("Could not load activities", err);
        }
    };

    const handleOpenActivityDialog = (activity = null, selectedStandId = null) => {
        if (activity) {
            setEditingActivity(activity);
            setActivityForm({
                stand_id: activity.stand_id,
                activity_type: activity.activity_type,
                sub_type: activity.sub_type || '',
                status: activity.status,
                planned_year: activity.planned_year,
                planned_quarter: activity.planned_quarter,
                priority: activity.priority,
                performer_type: activity.performer_type,
                description: activity.description || ''
            });
            setFinancialItems(activity.financials || []);
        } else {
            setEditingActivity(null);
            const quarter = Math.floor(new Date().getMonth() / 3) + 1;
            setActivityForm({
                stand_id: selectedStandId,
                activity_type: 'Planting',
                sub_type: '',
                status: 'planned',
                planned_year: new Date().getFullYear(),
                planned_quarter: quarter,
                priority: 'medium',
                performer_type: 'contractor',
                description: ''
            });
            setFinancialItems([]);
        }
        setActivityDialogOpen(true);
    };

    const handleCloseActivityDialog = () => {
        setActivityDialogOpen(false);
        setEditingActivity(null);
    };

    const handleSaveActivity = async () => {
        if (!activityForm.stand_id) {
            setMessage({ text: 'Please select a forest stand first', type: 'warning', open: true });
            return;
        }

        try {
            const midQuarterMonth = (activityForm.planned_quarter - 1) * 3 + 1;
            const planned_date = new Date(activityForm.planned_year, midQuarterMonth, 15).toISOString().split('T')[0];

            const activityData = { ...activityForm, planned_date };

            if (editingActivity) {
                await updateActivity(editingActivity.id, activityData, financialItems);
                setMessage({ text: 'Activity updated!', type: 'success', open: true });
            } else {
                await createActivity(activityData, financialItems);
                setMessage({ text: 'Activity created!', type: 'success', open: true });
            }

            await loadActivities(farmId);
            handleCloseActivityDialog();
        } catch (err) {
            console.error("Error saving activity:", err);
            setMessage({ text: 'Failed to save activity', type: 'error', open: true });
        }
    };

    const handleDeleteActivity = async (activityId) => {
        if (!window.confirm('Are you sure you want to delete this activity?')) return;
        try {
            await deleteActivity(activityId);
            setMessage({ text: 'Activity deleted!', type: 'success', open: true });
            loadActivities(farmId);
        } catch (err) {
            console.error("Error deleting activity:", err);
            setMessage({ text: 'Failed to delete activity', type: 'error', open: true });
        }
    };

    const handleAddFinancialItem = () => {
        setFinancialItems([...financialItems, { item_type: 'cost', category: '', amount: 0, unit: 'SEK' }]);
    };

    const handleRemoveFinancialItem = (index) => {
        setFinancialItems(financialItems.filter((_, i) => i !== index));
    };

    const handleFinancialItemChange = (index, field, value) => {
        const updated = [...financialItems];
        updated[index][field] = value;
        setFinancialItems(updated);
    };

    const updateSuggestedFinancials = (forestStands, userSettings) => {
        const stand = forestStands.features.find(f => f.id === activityForm.stand_id);
        if (!stand || !userSettings) return;

        const area = stand.properties.area_hectares;
        const volume_ha = stand.properties.volume_m3sk_ha;
        const total_volume = area * volume_ha;

        let suggested = [];
        let params = {};

        // Helper to get safe numeric value
        const num = (v) => (v === undefined || v === null || isNaN(v)) ? 0 : Number(v);

        if (activityForm.activity_type === 'Final Felling') {
            const h = userSettings.harvesting;
            const o = userSettings.operations;

            params = { 
                timber_percentage: num(h.timber_percentage), 
                timber_price_m3sk: num(h.timber_price_m3sk), 
                pulpwood_percentage: num(h.pulpwood_percentage), 
                pulpwood_price_m3sk: num(h.pulpwood_price_m3sk), 
                grot_factor: num(h.grot_factor), 
                grot_price_m3s: num(h.grot_price_m3s), 
                felling_start_cost: num(o.felling_start_cost), 
                hourly_machine_rate: num(o.hourly_machine_rate), 
                felling_hours_per_ha: num(o.felling_hours_per_ha), 
                yield: Math.round(total_volume)
            };
            suggested = [
                { item_type: 'revenue', category: 'Timber', amount: Math.round(params.yield * (params.timber_percentage / 100) * params.timber_price_m3sk), unit: 'SEK' },
                { item_type: 'revenue', category: 'Pulpwood', amount: Math.round(params.yield * (params.pulpwood_percentage / 100) * params.pulpwood_price_m3sk), unit: 'SEK' },
                { item_type: 'revenue', category: 'GROT', amount: Math.round(params.yield * params.grot_factor * params.grot_price_m3s), unit: 'SEK' },
                { item_type: 'cost', category: 'Machine Start Cost', amount: params.felling_start_cost, unit: 'SEK' },
                { item_type: 'cost', category: 'Harvesting & Forwarding', amount: Math.round(area * params.felling_hours_per_ha * params.hourly_machine_rate), unit: 'SEK' }
            ];
        } else if (activityForm.activity_type === 'Planting') {
            const s = userSettings.silviculture;
            params = { planting_density_per_ha: num(s.planting_density_per_ha), cost_per_plant: num(s.cost_per_plant) };
            suggested = [
                { item_type: 'cost', category: 'Plants', amount: Math.round(area * params.planting_density_per_ha * params.cost_per_plant), unit: 'SEK' },
                { item_type: 'cost', category: 'Planting Labor', amount: Math.round(area * params.planting_density_per_ha * 2.0), unit: 'SEK' }
            ];
        } else if (activityForm.activity_type === 'Clearing') {
            const o = userSettings.operations;
            params = { clearing_rate_per_ha: num(o.clearing_rate_per_ha) };
            suggested = [
                { item_type: 'cost', category: 'Clearing Labor', amount: Math.round(area * params.clearing_rate_per_ha), unit: 'SEK' }
            ];
        } else if (activityForm.activity_type === 'Thinning') {
            const o = userSettings.operations;
            const thinning_yield = Math.round(area * 50);
            params = { 
                thinning_yield, 
                hourly_machine_rate: num(o.hourly_machine_rate), 
                thinning_hours_per_ha: num(o.thinning_hours_per_ha), 
                thinning_start_cost: num(o.thinning_start_cost) 
            };
            suggested = [
                { item_type: 'revenue', category: 'Pulpwood (Thinning)', amount: Math.round(thinning_yield * num(userSettings.harvesting.pulpwood_price_m3sk)), unit: 'SEK' },
                { item_type: 'cost', category: 'Machine Start Cost', amount: params.thinning_start_cost, unit: 'SEK' },
                { item_type: 'cost', category: 'Thinning Operations', amount: Math.round(area * params.thinning_hours_per_ha * params.hourly_machine_rate), unit: 'SEK' }
            ];
        }

        setFinancialItems(suggested);
        setCalculationParams(params);
    };

    const handleParamChange = (key, value, forestStands, userSettings) => {
        // Ensure numeric value or empty string for stability
        let numericValue = value === '' ? '' : Number(value);
        if (numericValue !== '' && isNaN(numericValue)) numericValue = 0;
        
        const updatedParams = { ...calculationParams, [key]: numericValue };
        setCalculationParams(updatedParams);
        
        // Use a safe version for calculations
        const p = {};
        Object.keys(updatedParams).forEach(k => {
            p[k] = (updatedParams[k] === '' || isNaN(updatedParams[k])) ? 0 : updatedParams[k];
        });
        
        const stand = forestStands.features.find(f => f.id === activityForm.stand_id);
        const area = stand ? stand.properties.area_hectares : 0;
        let newItems = [];

        if (activityForm.activity_type === 'Final Felling') {
            const p = updatedParams;
            newItems = [
                { item_type: 'revenue', category: 'Timber', amount: Math.round(p.yield * (p.timber_percentage / 100) * p.timber_price_m3sk), unit: 'SEK' },
                { item_type: 'revenue', category: 'Pulpwood', amount: Math.round(p.yield * (p.pulpwood_percentage / 100) * p.pulpwood_price_m3sk), unit: 'SEK' },
                { item_type: 'revenue', category: 'GROT', amount: Math.round(p.yield * p.grot_factor * p.grot_price_m3s), unit: 'SEK' },
                { item_type: 'cost', category: 'Machine Start Cost', amount: p.felling_start_cost, unit: 'SEK' },
                { item_type: 'cost', category: 'Harvesting & Forwarding', amount: Math.round(area * p.felling_hours_per_ha * p.hourly_machine_rate), unit: 'SEK' }
            ];
        } else if (activityForm.activity_type === 'Planting') {
            newItems = [
                { item_type: 'cost', category: 'Plants', amount: Math.round(area * updatedParams.planting_density_per_ha * updatedParams.cost_per_plant), unit: 'SEK' },
                { item_type: 'cost', category: 'Planting Labor', amount: Math.round(area * updatedParams.planting_density_per_ha * 2.0), unit: 'SEK' }
            ];
        } else if (activityForm.activity_type === 'Clearing') {
            newItems = [
                { item_type: 'cost', category: 'Clearing Labor', amount: Math.round(area * updatedParams.clearing_rate_per_ha), unit: 'SEK' }
            ];
        } else if (activityForm.activity_type === 'Thinning') {
            const p = updatedParams;
            newItems = [
                { item_type: 'revenue', category: 'Pulpwood (Thinning)', amount: Math.round(p.thinning_yield * userSettings.harvesting.pulpwood_price_m3sk), unit: 'SEK' },
                { item_type: 'cost', category: 'Machine Start Cost', amount: p.thinning_start_cost, unit: 'SEK' },
                { item_type: 'cost', category: 'Thinning Operations', amount: Math.round(area * p.thinning_hours_per_ha * p.hourly_machine_rate), unit: 'SEK' }
            ];
        }

        setFinancialItems(newItems);
    };

    const handleActivityClick = (activity, setActiveTab) => {
        setActiveTab(2);
        setFilters({
            years: [activity.planned_year],
            standIds: [activity.stand_id],
            activityTypes: [activity.activity_type],
            statuses: [activity.status],
            priorities: [activity.priority]
        });
        setHighlightedStandIds([activity.stand_id]);
    };

    const handleViewAllForStand = (standId, setActiveTab) => {
        setActiveTab(2);
        setFilters({
            years: [],
            standIds: [standId],
            activityTypes: [],
            statuses: [],
            priorities: []
        });
        setHighlightedStandIds([standId]);
    };

    const clearFilters = () => {
        setFilters({ years: [], standIds: [], activityTypes: [], statuses: [], priorities: [] });
        setHighlightedStandIds([]);
    };

    const toggleFilter = (category, value) => {
        setFilters(prev => {
            const current = prev[category];
            const newValues = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
            return { ...prev, [category]: newValues };
        });
    };

    const filteredActivities = useMemo(() => {
        let filtered = [...activities];
        if (filters.years.length > 0) filtered = filtered.filter(a => filters.years.includes(a.planned_year));
        if (filters.standIds.length > 0) filtered = filtered.filter(a => filters.standIds.includes(a.stand_id));
        if (filters.activityTypes.length > 0) filtered = filtered.filter(a => filters.activityTypes.includes(a.activity_type));
        if (filters.statuses.length > 0) filtered = filtered.filter(a => filters.statuses.includes(a.status));
        if (filters.priorities.length > 0) filtered = filtered.filter(a => filters.priorities.includes(a.priority));

        filtered.sort((a, b) => {
            if (sortBy === 'date') return (a.planned_year !== b.planned_year) ? a.planned_year - b.planned_year : a.planned_quarter - b.planned_quarter;
            if (sortBy === 'priority') {
                const order = { high: 3, medium: 2, low: 1 };
                return order[b.priority] - order[a.priority];
            }
            return (sortBy === 'type') ? a.activity_type.localeCompare(b.activity_type) : 0;
        });
        return filtered;
    }, [activities, filters, sortBy]);

    const timelineYears = useMemo(() => {
        if (activities.length === 0) return [new Date().getFullYear()];
        const years = activities.map(a => a.planned_year);
        const minYear = Math.min(...years);
        const maxYear = Math.max(...years, minYear + 4);
        const result = [];
        for (let y = minYear; y <= maxYear; y++) result.push(y);
        return result;
    }, [activities]);

    const uniqueStandsForFilter = useMemo(() => {
        const standsMap = new Map();
        activities.forEach(a => {
            if (!standsMap.has(a.stand_id)) standsMap.set(a.stand_id, { id: a.stand_id, number: a.stand_number });
        });
        return Array.from(standsMap.values()).sort((a, b) => a.number - b.number);
    }, [activities]);

    useEffect(() => {
        if (filters.standIds.length > 0) setHighlightedStandIds(filters.standIds);
        else setHighlightedStandIds([]);
    }, [filters.standIds]);

    return {
        activities, loadActivities,
        activityDialogOpen, setActivityDialogOpen,
        editingActivity, handleOpenActivityDialog, handleCloseActivityDialog,
        activityForm, setActivityForm,
        financialItems, setFinancialItems, calculationParams,
        handleSaveActivity, handleDeleteActivity,
        handleAddFinancialItem, handleRemoveFinancialItem, handleFinancialItemChange,
        updateSuggestedFinancials, handleParamChange,
        handleActivityClick, handleViewAllForStand,
        filters, setFilters, toggleFilter, clearFilters,
        sortBy, setSortBy, highlightedStandIds, setHighlightedStandIds,
        filteredActivities, timelineYears, uniqueStandsForFilter
    };
};
